import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { theme } from '../../theme';
import { Button } from '../../components/ui/Button';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import { FiUser, FiMail, FiBriefcase, FiShield } from 'react-icons/fi';

const PageContainer = styled.div`
  padding: ${theme.spacing[6]};
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: ${theme.spacing[6]};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const PageDescription = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.light.text.secondary};
  margin: 0;
`;

const ProfileCard = styled.div`
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.md};
  padding: ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[4]};
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
  padding-bottom: ${theme.spacing[6]};
  border-bottom: 1px solid ${theme.colors.light.border.medium};
`;

const Avatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: ${theme.borderRadius.full};
  background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.primary[700]});
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.white};
  font-weight: ${theme.typography.fontWeight.bold};
  font-size: ${theme.typography.fontSize['2xl']};
`;

const ProfileInfo = styled.div`
  flex: 1;
`;

const ProfileName = styled.h2`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.light.text.primary};
  margin: 0 0 ${theme.spacing[1]} 0;
`;

const ProfileRole = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
  margin: 0;
  text-transform: capitalize;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
`;

const InfoIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.md};
  background-color: ${theme.colors.light.background.tertiary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.primary[500]};
  flex-shrink: 0;
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoLabel = styled.p`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.secondary};
  margin: 0 0 ${theme.spacing[1]} 0;
  text-transform: uppercase;
  font-weight: ${theme.typography.fontWeight.semibold};
`;

const InfoValue = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.light.text.primary};
  margin: 0;
  word-break: break-word;
`;

const ActionSection = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  flex-wrap: wrap;
`;

const ProfilePage: React.FC = () => {
    const { user, userData, companyData, staffData } = useAuth();
    const navigate = useNavigate();

    const getInitials = (email: string | undefined, displayName: string | undefined) => {
        if (displayName) {
            return displayName
                .split(' ')
                .map(n => n[0])
                .join('')
                .toUpperCase()
                .slice(0, 2);
        }
        if (email) {
            return email[0].toUpperCase();
        }
        return 'U';
    };

    const getUserRole = () => {
        if (staffData) {
            return staffData.department || staffData.departments?.[0] || staffData.role || 'staff';
        }
        if (companyData) {
            return 'admin';
        }
        return userData?.role || 'user';
    };

    return (
        <DashboardLayout>
            <PageContainer>
                <PageHeader>
                    <PageTitle>Profile</PageTitle>
                    <PageDescription>Manage your account information and settings</PageDescription>
                </PageHeader>

                <ProfileCard>
                    <ProfileHeader>
                        <Avatar>
                            {getInitials(user?.email || userData?.email, userData?.displayName || user?.displayName || undefined)}
                        </Avatar>
                        <ProfileInfo>
                            <ProfileName>{user?.email || userData?.email || 'User'}</ProfileName>
                            <ProfileRole>{getUserRole()}</ProfileRole>
                        </ProfileInfo>
                    </ProfileHeader>

                    <InfoGrid>
                        <InfoItem>
                            <InfoIcon>
                                <FiMail size={20} />
                            </InfoIcon>
                            <InfoContent>
                                <InfoLabel>Email Address</InfoLabel>
                                <InfoValue>{user?.email || userData?.email || 'N/A'}</InfoValue>
                            </InfoContent>
                        </InfoItem>

                        <InfoItem>
                            <InfoIcon>
                                <FiUser size={20} />
                            </InfoIcon>
                            <InfoContent>
                                <InfoLabel>Display Name</InfoLabel>
                                <InfoValue>{userData?.displayName || user?.displayName || 'Not set'}</InfoValue>
                            </InfoContent>
                        </InfoItem>

                        <InfoItem>
                            <InfoIcon>
                                <FiBriefcase size={20} />
                            </InfoIcon>
                            <InfoContent>
                                <InfoLabel>Role</InfoLabel>
                                <InfoValue>{getUserRole()}</InfoValue>
                            </InfoContent>
                        </InfoItem>

                        {companyData && (
                            <InfoItem>
                                <InfoIcon>
                                    <FiBriefcase size={20} />
                                </InfoIcon>
                                <InfoContent>
                                    <InfoLabel>Company</InfoLabel>
                                    <InfoValue>{companyData.companyName || companyData.name || 'N/A'}</InfoValue>
                                </InfoContent>
                            </InfoItem>
                        )}
                    </InfoGrid>

                    <ActionSection>
                        <Button
                            variant="primary"
                            onClick={() => navigate('/change-password')}
                        >
                            <FiShield size={16} />
                            Change Password
                        </Button>
                    </ActionSection>
                </ProfileCard>
            </PageContainer>
        </DashboardLayout>
    );
};

export default ProfilePage;
